import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  RefreshIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  SecurityIcon,
  AlertIcon,
  CheckIcon,
} from '../components/common/Icons';

/**
 * RagCollectionsPage (WQ-311) — the collection-level harness-tagging surface.
 *
 * Sets a RAG collection's DEFAULT HARNESS: the access-classification template
 * future-ingested chunks inherit (Phase 5.3). Best-of-breed
 * data-classification-with-inheritance (Purview / shared-drive policy / Lake
 * Formation): a single ordered-ladder sensitivity select, governed LIVE
 * multi-selects for functional areas + authorized roles, secure-by-default
 * (internal / nothing-granted / retrieval_only / derive-routing), and routing as
 * an optional override constrained to the validator FLOOR. Inline validation
 * mirrors the server-authoritative validate_harness so a self-contradictory
 * policy is blocked before submit — the server is the backstop.
 *
 * CRUD → /api/rag/collections (tenant-scoped via RLS). Live sources:
 * /api/admin/roles/functional-areas (areas) + /api/admin/roles (roles).
 */

// Ordered clearance ladder (public < internal < confidential < restricted).
const SENSITIVITY_OPTIONS = [
  { value: 'public', label: 'Public', rank: 0 },
  { value: 'internal', label: 'Internal', rank: 1 },
  { value: 'confidential', label: 'Confidential', rank: 2 },
  { value: 'restricted', label: 'Restricted', rank: 3 },
];

const USAGE_SCOPE_OPTIONS = [
  { value: 'retrieval_only', label: 'Retrieval only' },
  { value: 'training_permitted', label: 'Training permitted' },
];

const ROUTING_OPTIONS = [
  { value: 'local_only', label: 'Local only' },
  { value: 'approved_origins', label: 'Approved origins' },
  { value: 'any', label: 'Any' },
];

interface RagCollection {
  id: string;
  tenant_id: string;
  name: string;
  default_sensitivity: string;
  default_functional_areas: string[];
  default_authorized_roles: string[];
  default_model_routing: string | null;
  default_usage_scope: string;
  default_abac: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
}

interface FunctionalArea {
  area_key: string;
  label: string;
}

interface RoleOption {
  id: string;
  display_name: string;
}

interface FormState {
  name: string;
  default_sensitivity: string;
  default_functional_areas: string[];
  default_authorized_roles: string[];
  default_model_routing: string; // '' = derive from sensitivity (null)
  default_usage_scope: string;
  default_abac: string; // raw JSON text; '' = none (DEF-051 — advanced only)
}

const EMPTY_FORM: FormState = {
  name: '',
  default_sensitivity: 'internal',
  default_functional_areas: [],
  default_authorized_roles: [],
  default_model_routing: '',
  default_usage_scope: 'retrieval_only',
  default_abac: '',
};

// Routing options valid for a given sensitivity (the validator FLOOR, mirrored
// client-side). null/'' (derive) is always allowed. Confidential ≠ any;
// Restricted ⇒ local_only only.
function allowedRoutingFor(sensitivity: string): string[] {
  if (sensitivity === 'restricted') return ['local_only'];
  if (sensitivity === 'confidential') return ['local_only', 'approved_origins'];
  return ['local_only', 'approved_origins', 'any'];
}

// Inline mirror of validate_harness (server is authoritative).
function harnessViolations(f: FormState): string[] {
  const v: string[] = [];
  const s = f.default_sensitivity;
  const r = f.default_model_routing;
  if ((s === 'confidential' || s === 'restricted') && r === 'any') {
    v.push(`model_routing 'any' is not permitted for sensitivity '${s}'.`);
  }
  if (s === 'restricted' && r && r !== 'local_only') {
    v.push("sensitivity 'restricted' requires model_routing 'local_only'.");
  }
  if (f.default_usage_scope === 'training_permitted' && s === 'restricted') {
    v.push("usage_scope 'training_permitted' is not allowed on 'restricted' content.");
  }
  if (f.default_abac.trim()) {
    try {
      JSON.parse(f.default_abac);
    } catch {
      v.push('Advanced ABAC must be valid JSON (or left empty).');
    }
  }
  return v;
}

const RagCollectionsPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [collections, setCollections] = useState<RagCollection[]>([]);
  const [functionalAreas, setFunctionalAreas] = useState<FunctionalArea[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noTenant, setNoTenant] = useState(false);

  // Unified create/edit form. editingId === null → create.
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchCollections = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await authFetch('/api/rag/collections');
      if (res.status === 403) {
        setNoTenant(true);
        setCollections([]);
        return;
      }
      if (!res.ok) throw new Error(`Failed to load collections (${res.status})`);
      const data = await res.json();
      setNoTenant(false);
      setCollections(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collections');
    } finally {
      setIsLoading(false);
    }
  }, [authFetch]);

  const fetchFunctionalAreas = useCallback(async () => {
    try {
      const res = await authFetch('/api/admin/roles/functional-areas');
      if (res.ok) {
        const data = await res.json();
        setFunctionalAreas(Array.isArray(data) ? data : []);
      }
    } catch {
      // Non-fatal — the multi-select renders empty; backend still validates.
    }
  }, [authFetch]);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await authFetch('/api/admin/roles');
      if (res.ok) {
        const data = await res.json();
        setRoles(
          Array.isArray(data)
            ? data.map((r: { id: string; display_name?: string }) => ({
                id: r.id,
                display_name: r.display_name || r.id,
              }))
            : []
        );
      }
    } catch {
      // Non-fatal — backend still validates roles fail-closed.
    }
  }, [authFetch]);

  useEffect(() => {
    if (authLoading) return;
    fetchCollections();
    fetchFunctionalAreas();
    fetchRoles();
  }, [authLoading, fetchCollections, fetchFunctionalAreas, fetchRoles]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (c: RagCollection) => {
    setEditingId(c.id);
    setForm({
      name: c.name,
      default_sensitivity: c.default_sensitivity,
      default_functional_areas: c.default_functional_areas || [],
      default_authorized_roles: c.default_authorized_roles || [],
      default_model_routing: c.default_model_routing || '',
      default_usage_scope: c.default_usage_scope,
      default_abac: c.default_abac ? JSON.stringify(c.default_abac, null, 2) : '',
    });
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  // When sensitivity changes, drop a now-invalid routing override to "derive".
  const setSensitivity = (value: string) => {
    setForm(prev => {
      const allowed = allowedRoutingFor(value);
      const routing =
        prev.default_model_routing && !allowed.includes(prev.default_model_routing)
          ? ''
          : prev.default_model_routing;
      return { ...prev, default_sensitivity: value, default_model_routing: routing };
    });
  };

  const toggleArea = (areaKey: string) => {
    setForm(prev => ({
      ...prev,
      default_functional_areas: prev.default_functional_areas.includes(areaKey)
        ? prev.default_functional_areas.filter(a => a !== areaKey)
        : [...prev.default_functional_areas, areaKey],
    }));
  };

  const toggleRole = (roleId: string) => {
    setForm(prev => ({
      ...prev,
      default_authorized_roles: prev.default_authorized_roles.includes(roleId)
        ? prev.default_authorized_roles.filter(r => r !== roleId)
        : [...prev.default_authorized_roles, roleId],
    }));
  };

  const violations = harnessViolations(form);
  const canSave = form.name.trim().length > 0 && violations.length === 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setFormError(null);
    const abacParsed =
      form.default_abac.trim() ? JSON.parse(form.default_abac) : null;

    try {
      let res: Response;
      if (editingId === null) {
        // Create — secure defaults honored; omit routing/abac when empty.
        res = await authFetch('/api/rag/collections', {
          method: 'POST',
          body: JSON.stringify({
            name: form.name.trim(),
            default_sensitivity: form.default_sensitivity,
            default_functional_areas: form.default_functional_areas,
            default_authorized_roles: form.default_authorized_roles,
            default_usage_scope: form.default_usage_scope,
            default_model_routing: form.default_model_routing || null,
            default_abac: abacParsed,
          }),
        });
      } else {
        // Update — full replace from the form. Empty routing/abac clear via the
        // backend sentinels (null alone is "unchanged" in a partial update).
        res = await authFetch(`/api/rag/collections/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: form.name.trim(),
            default_sensitivity: form.default_sensitivity,
            default_functional_areas: form.default_functional_areas,
            default_authorized_roles: form.default_authorized_roles,
            default_usage_scope: form.default_usage_scope,
            default_model_routing: form.default_model_routing || null,
            clear_model_routing: !form.default_model_routing,
            default_abac: abacParsed,
            clear_abac: abacParsed === null,
          }),
        });
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const detail = body?.detail;
        const msg =
          detail && typeof detail === 'object' && Array.isArray(detail.violations)
            ? detail.violations.join('; ')
            : typeof detail === 'string'
              ? detail
              : `Save failed (${res.status})`;
        throw new Error(msg);
      }
      closeForm();
      await fetchCollections();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c: RagCollection) => {
    const ok = window.confirm(
      `Delete collection "${c.name}"?\n\nThis cascade-deletes any chunks that ` +
        `inherit it (none exist until Phase 5.3 ingestion).`
    );
    if (!ok) return;
    try {
      const res = await authFetch(`/api/rag/collections/${c.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      await fetchCollections();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // ----------------------------------------------------------------------- //
  // Render
  // ----------------------------------------------------------------------- //
  const label = (s: string) =>
    SENSITIVITY_OPTIONS.find(o => o.value === s)?.label || s;

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SecurityIcon size={22} />
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>RAG Collections</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={fetchCollections} title="Refresh">
            <RefreshIcon size={16} /> Refresh
          </button>
          {!noTenant && (
            <button className="btn btn-primary" onClick={openCreate}>
              <PlusIcon size={16} /> New Collection
            </button>
          )}
        </div>
      </div>
      <p style={{ color: 'var(--text-secondary)', marginTop: 0, marginBottom: 20, fontSize: 13 }}>
        Set a collection's <strong>default harness</strong> — the access-classification template
        its chunks inherit at ingestion. Secure-by-default: leaving fields as-is grants the least.
      </p>

      {error && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 12, marginBottom: 16, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 8, color: '#fca5a5' }}>
          <AlertIcon size={16} /> {error}
        </div>
      )}

      {noTenant ? (
        <div style={{ padding: 24, background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
          <strong style={{ color: 'var(--text-primary)' }}>No tenant context.</strong> RAG
          collection management is tenant-scoped. Sign in as a tenant admin (or a user provisioned
          within a tenant) to manage that tenant's collections.
        </div>
      ) : isLoading ? (
        <div style={{ color: 'var(--text-secondary)', padding: 24 }}>Loading collections…</div>
      ) : collections.length === 0 ? (
        <div style={{ padding: 24, background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
          No collections yet. Create one to define a default harness the future-ingested chunks inherit.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {collections.map(c => (
            <div key={c.id} style={{ padding: 16, background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{c.name}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 12 }}>
                  <span style={{ padding: '2px 8px', borderRadius: 4, background: 'rgba(124,58,237,0.15)', color: 'var(--text-secondary)' }}>
                    {label(c.default_sensitivity)}
                  </span>
                  <span style={{ padding: '2px 8px', borderRadius: 4, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                    {c.default_usage_scope}
                  </span>
                  <span style={{ padding: '2px 8px', borderRadius: 4, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                    routing: {c.default_model_routing || 'derive'}
                  </span>
                  <span style={{ padding: '2px 8px', borderRadius: 4, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                    areas: {c.default_functional_areas.length || '—'}
                  </span>
                  <span style={{ padding: '2px 8px', borderRadius: 4, background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                    roles: {c.default_authorized_roles.length || '—'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)} title="Edit">
                  <EditIcon size={14} />
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => handleDelete(c)} title="Delete">
                  <TrashIcon size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <div style={{ marginTop: 24, padding: 20, background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, marginTop: 0, marginBottom: 16 }}>
            {editingId === null ? 'New Collection' : 'Edit Collection'}
          </h2>

          {/* Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Name</label>
            <input
              type="text"
              value={form.name}
              maxLength={255}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Finance Knowledge Base"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
            />
          </div>

          {/* Sensitivity (single-select ordered ladder) */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Sensitivity (ordered: {SENSITIVITY_OPTIONS.map(o => o.label).join(' < ')})
            </label>
            <select
              value={form.default_sensitivity}
              onChange={e => setSensitivity(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
            >
              {SENSITIVITY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Functional areas (live multi-select) */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Functional Areas ({form.default_functional_areas.length} selected)
            </label>
            <div style={{ padding: 12, background: 'var(--bg-tertiary)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
                No areas = nothing granted (secure default). A chunk is retrievable only by a user whose role grants an overlapping area.
              </small>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 6 }}>
                {functionalAreas.map(area => (
                  <label key={area.area_key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '4px 8px', borderRadius: 4, background: form.default_functional_areas.includes(area.area_key) ? 'rgba(59,130,246,0.1)' : 'transparent' }}>
                    <input type="checkbox" checked={form.default_functional_areas.includes(area.area_key)} onChange={() => toggleArea(area.area_key)} style={{ cursor: 'pointer' }} />
                    <span>{area.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Authorized roles (live multi-select) */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>
              Authorized Roles ({form.default_authorized_roles.length} selected)
            </label>
            <div style={{ padding: 12, background: 'var(--bg-tertiary)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
                No roles = no role restriction beyond the other axes.
              </small>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6 }}>
                {roles.map(r => (
                  <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, padding: '4px 8px', borderRadius: 4, background: form.default_authorized_roles.includes(r.id) ? 'rgba(59,130,246,0.1)' : 'transparent' }}>
                    <input type="checkbox" checked={form.default_authorized_roles.includes(r.id)} onChange={() => toggleRole(r.id)} style={{ cursor: 'pointer' }} />
                    <span>{r.display_name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
            {/* Usage scope */}
            <div style={{ flex: '1 1 240px' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Usage Scope</label>
              <select
                value={form.default_usage_scope}
                onChange={e => setForm(prev => ({ ...prev, default_usage_scope: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
              >
                {USAGE_SCOPE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Model routing (optional override, floor-constrained) */}
            <div style={{ flex: '1 1 240px' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Model Routing (optional)</label>
              <select
                value={form.default_model_routing}
                onChange={e => setForm(prev => ({ ...prev, default_model_routing: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
              >
                <option value="">(derive from sensitivity)</option>
                {ROUTING_OPTIONS.filter(o => allowedRoutingFor(form.default_sensitivity).includes(o.value)).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Advanced: ABAC raw JSON (DEF-051 — no governed vocabulary yet) */}
          <details style={{ marginBottom: 16 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
              Advanced: ABAC conditions (raw JSON, optional)
            </summary>
            <textarea
              value={form.default_abac}
              onChange={e => setForm(prev => ({ ...prev, default_abac: e.target.value }))}
              placeholder='e.g. {"require": {"managed_device": true}}'
              rows={4}
              style={{ width: '100%', marginTop: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 12 }}
            />
          </details>

          {/* Inline validation (mirrors validate_harness; server is authoritative) */}
          {violations.length > 0 && (
            <div style={{ padding: 12, marginBottom: 12, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 8, color: '#fca5a5', fontSize: 13 }}>
              {violations.map((v, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <AlertIcon size={14} /> {v}
                </div>
              ))}
            </div>
          )}
          {formError && (
            <div style={{ padding: 12, marginBottom: 12, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: 8, color: '#fca5a5', fontSize: 13 }}>
              {formError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={closeForm} disabled={saving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={!canSave}>
              <CheckIcon size={16} /> {saving ? 'Saving…' : editingId === null ? 'Create' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RagCollectionsPage;
